import OpenStratifyButton from '../../components/OpenStratifyButton';
import { billingReturnTrace } from '../../billing/appReturn';
import React, { useRef, useState } from "react";
import Link from "../../components/Link";
import AccountLayout from "../../account/AccountLayout";
import { useAccount } from "../../account/AccountProvider";
import { AccountButton, AccountCard, AccountStatus } from "../../account/AccountUi";
import { createCheckoutSession, createPortalSession } from "../../billing/billingApiClient";
import { navigateAllowlistedBillingUrl } from "../../billing/redirectAllowlist";
import {
  billingWebConfig,
  isPublicWebCheckoutEnabled,
} from "../../billing/billingConfig";
import {
  DISPLAY_PLANS,
  formatOfferPrice,
  newCheckoutOfferForPlan,
  paidPlanIds,
} from "../../billing/planCatalog";
import { formatDate } from "../../account/entitlementAdapter";
import { LEGAL_RELATED_LINKS } from "../../config/legal";

export default function SubscriptionBillingPage() {
  return (
    <AccountLayout
      title="Subscription & billing"
      description="Your current Stratify plan and how to manage it."
      path="/account/billing"
    >
      <OpenStratifyButton />
      <SubscriptionBillingBody />
    </AccountLayout>
  );
}

function SubscriptionBillingBody() {
  const { entitlement, entitlementState, reloadEntitlement } = useAccount();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [tone, setTone] = useState("info");
  const inFlight = useRef(false);
  const idempotencyRef = useRef(null);
  const checkoutEnabled = isPublicWebCheckoutEnabled();

  function setStatus(text, nextTone = "info") {
    setMessage(text);
    setTone(nextTone);
  }

  async function onUpgrade(offerKey) {
    if (!entitlement?.canCheckout || !checkoutEnabled || inFlight.current) return;
    billingReturnTrace("upgrade_start");
    inFlight.current = true;
    setBusy(true);
    setStatus(null);
    if (!idempotencyRef.current) {
      idempotencyRef.current =
        typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `web-${Date.now()}`;
    }
    try {
      const result = await createCheckoutSession({
        offerKey,
        idempotencyKey: idempotencyRef.current,
      });
      if (result.result === "manage_existing_subscription") {
        idempotencyRef.current = null;
        setStatus("You already have an active Stripe subscription. Use Manage subscription.");
        return;
      }
      if (result.result === "active_subscription_other_source") {
        idempotencyRef.current = null;
        if (entitlement.appleManaged) {
          setStatus("Your subscription is managed through Apple. Do not start a second Stripe subscription.");
        } else {
          setStatus("An active subscription already exists from another source. Contact support if you need help.");
        }
        return;
      }
      billingReturnTrace("upgrade_result", { status: result.result });
      if (result.result === 'subscription_already_active') {
        await reloadEntitlement();
        setStatus('Your requested plan is active.');
        idempotencyRef.current = null;
        return;
      }
      if (result.checkoutUrl || result.portalUrl) {
        const ok = navigateAllowlistedBillingUrl(result.checkoutUrl || result.portalUrl);
        if (!ok) {
          idempotencyRef.current = null;
          setStatus("Checkout redirect was blocked for safety.", "error");
        }
        return;
      }
      idempotencyRef.current = null;
      setStatus("Unexpected Checkout response.", "error");
    } catch (err) {
      idempotencyRef.current = null;
      setStatus(err?.message || "Unable to start Checkout.", "error");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  async function onManageStripe() {
    billingReturnTrace("portal_start");
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const result = await createPortalSession();
      if (result.portalUrl) {
        const ok = navigateAllowlistedBillingUrl(result.portalUrl);
        if (!ok) setStatus("Portal redirect was blocked for safety.", "error");
      } else if (result.manageTarget === "apple_subscriptions" || entitlement?.appleManaged) {
        window.open(billingWebConfig.appleSubscriptionsUrl, "_blank", "noopener,noreferrer");
      } else {
        setStatus("This subscription is not managed via Stripe. Contact support.");
      }
    } catch (err) {
      setStatus(err?.message || "Unable to open billing portal.", "error");
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  const confirmed = entitlement?.freshness === "ready";
  const unknown = !entitlement || entitlement.status === "unknown" || entitlement.status === "degraded" || entitlement.freshness === "stale";

  return (
      <div className="space-y-5">
        <AccountCard>
          {entitlementState === "loading" && !entitlement ? (
            <AccountStatus>Checking your subscription…</AccountStatus>
          ) : entitlement ? (
            <>
              <p className="text-lg font-semibold text-ink">{entitlement.headline}</p>
              <p className="mt-2 text-sm text-muted">{entitlement.detail}</p>
              {entitlement.willRenew === false && entitlement.currentPeriodEnd ? (
                <p className="mt-2 text-sm text-muted">
                  Access continues until {formatDate(entitlement.currentPeriodEnd)}. This is not a downgrade.
                </p>
              ) : null}
              {entitlement.graceEndsAt ? (
                <p className="mt-2 text-sm text-muted">Grace ends {formatDate(entitlement.graceEndsAt)}.</p>
              ) : null}
            </>
          ) : (
            <AccountStatus>We couldn’t confirm your subscription.</AccountStatus>
          )}
          <AccountButton variant="secondary" className="mt-4" onClick={reloadEntitlement}>
            Retry
          </AccountButton>
          {message ? <AccountStatus tone={tone}>{message}</AccountStatus> : null}
        </AccountCard>

        <AccountCard>
          <h2 className="text-base font-semibold text-ink">Manage billing</h2>
          {unknown ? (
            <p className="mt-2 text-sm text-muted">
              We will not invite a new purchase until your current subscription is confirmed.
            </p>
          ) : entitlement?.manageTarget === "stripe_portal" ? (
            <div className="mt-3">
              <AccountButton disabled={busy} onClick={onManageStripe}>
                Manage subscription
              </AccountButton>
            </div>
          ) : entitlement?.manageTarget === "apple_subscriptions" || entitlement?.appleManaged ? (
            <div className="mt-3 space-y-3">
              <p className="text-sm text-muted">Managed through Apple. Website Checkout is not offered for this account.</p>
              <a
                href={billingWebConfig.appleSubscriptionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center justify-center rounded-xl px-4 text-sm font-semibold text-ink/80 ring-1 ring-ink/10"
              >
                Open Apple Subscriptions
              </a>
            </div>
          ) : entitlement?.manageTarget === "support" ? (
            <p className="mt-2 text-sm text-muted">
              This subscription needs a review.{" "}
              <Link to="/support" className="font-semibold text-ink underline underline-offset-4">
                Contact support
              </Link>
              .
            </p>
          ) : (
            <p className="mt-2 text-sm text-muted">No billing management action is available for this account.</p>
          )}
        </AccountCard>

        {confirmed && entitlement?.canCheckout ? (
          <AccountCard>
            <h2 className="text-base font-semibold text-ink">Upgrade</h2>
            <p className="mt-2 text-sm text-muted">
              {checkoutEnabled
                ? "Website Checkout is available for this account."
                : "Website Checkout is not open for public purchases yet. The App Store remains the current paid path."}
            </p>
            <p className="mt-4 text-sm text-muted">New website subscriptions are billed monthly in USD.</p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {paidPlanIds().map((planId) => {
                const plan = DISPLAY_PLANS[planId];
                const offer = newCheckoutOfferForPlan(planId);
                return (
                  <div key={planId} className="rounded-xl border border-ink/8 p-4">
                    <p className="font-semibold text-ink">{plan.displayName}</p>
                    <p className="mt-1 text-sm text-muted">{formatOfferPrice(offer)}</p>
                    <AccountButton
                      className="mt-3 w-full"
                      disabled={busy || !checkoutEnabled || !offer}
                      onClick={() => offer && onUpgrade(offer.offerKey)}
                    >
                      Subscribe
                    </AccountButton>
                  </div>
                );
              })}
            </div>
          </AccountCard>
        ) : null}

        <div className="flex flex-wrap gap-3">
          {LEGAL_RELATED_LINKS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="text-sm font-semibold text-hint-deep underline underline-offset-4"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
  );
}
